import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, RotateCcw, ShieldCheck, HelpCircle } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useSeo } from "../lib/seo";

const TABS = [
  { value: "favorite", label: "Coup de cœur", filter: "?is_favorite=true" },
];

const SETTINGS_CACHE_KEY = "berulia_settings_cache";
const SETTINGS_DEFAULTS = {
  hero_image_url: "",
  hero_cta_label: "Découvrir la collection",
  hero_cta_link: "/boutique",
};

function readSettingsCache() {
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return SETTINGS_DEFAULTS;
    return { ...SETTINGS_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return SETTINGS_DEFAULTS;
  }
}

export default function Home() {
  const [tab, setTab] = useState("favorite");
  const [list, setList] = useState([]);
  const [news, setNews] = useState([]);
  const [settings, setSettings] = useState(readSettingsCache);

  useSeo({
    title: "BERULIA — Prêt-à-porter féminin",
    description: "Boutique en ligne française de prêt-à-porter féminin : robes, tops, jupes, pantalons, accessoires et bijoux. Livraison offerte dès 79 €, retours sous 14 jours.",
    path: "/",
  });

  useEffect(() => {
    const f = TABS.find(t => t.value === tab);
    api.get(`/products${f.filter}`).then(r => setList(r.data.slice(0, 8)));
  }, [tab]);

  useEffect(() => {
    api.get("/products?is_new=true&sort=newest").then(r => setNews(r.data.slice(0, 8)));
  }, []);

  useEffect(() => {
    api.get("/settings").then(r => {
      const next = { ...SETTINGS_DEFAULTS, ...r.data };
      setSettings(next);
      try { localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(next)); } catch {}
    }).catch(() => {});
  }, []);

  return (
    <>
      {/* Hero romantique */}
      <section className="relative">
        <div className="relative h-[80vh] md:h-[92vh] overflow-hidden bg-berulia-beigewarm">
          {settings.hero_image_url && (
            <img src={settings.hero_image_url} alt="BERULIA" className="absolute inset-0 w-full h-full object-cover"/>
          )}
          <div className="relative z-10 h-full flex items-end px-8 md:px-20 max-w-[1500px] mx-auto pb-20">
            <div className="animate-fade-up">
              <Link to={settings.hero_cta_link || "/boutique"} data-testid="hero-shop-btn" className="btn-romance">{settings.hero_cta_label || "Découvrir la collection"}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3 teaser cards */}
      <section className="px-6 md:px-12 py-20 max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 border-b border-berulia-border pb-5">
          <h2 className="font-heading text-2xl md:text-3xl pb-2 border-b-2 border-berulia-rosedeep text-berulia-inkdeep">Nouveautés</h2>
          <Link to="/categorie/nouveautes" className="overline link-underline text-berulia-rosedeep">Voir tout →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-12">
          {news.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
        </div>
      </section>

      {/* Tabbed grid */}
      <section className="px-6 md:px-12 pb-20 max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 border-b border-berulia-border pb-5">
          <div className="flex gap-10">
            {TABS.map(t => (
              <button key={t.value} onClick={() => setTab(t.value)}
                data-testid={`home-tab-${t.value}`}
                className={`font-heading text-2xl md:text-3xl pb-2 transition border-b-2 ${tab===t.value ? "border-berulia-rosedeep text-berulia-inkdeep" : "border-transparent text-berulia-inkmuted hover:text-berulia-inkdeep"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <Link to="/boutique" className="overline link-underline text-berulia-rosedeep">Voir tout →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-12">
          {list.map((p,i) => <ProductCard key={p.id} product={p} index={i}/>)}
        </div>
      </section>

      {/* Trust band - pastel */}
      <section className="bg-berulia-ivory border-y border-berulia-border">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-14 grid md:grid-cols-4 gap-8 text-center">
          {[
            {Icon: Truck, title: "Livraison offerte", sub: "Dès 79 € en France métropolitaine", to: "/cgv"},
            {Icon: RotateCcw, title: "Retours sous 14 jours", sub: "Voir la politique", to: "/politique-retour"},
            {Icon: ShieldCheck, title: "Paiement sécurisé", sub: "Carte bancaire · Stripe"},
            {Icon: HelpCircle, title: "Besoin d'aide ?", sub: "contact@berulia.fr"},
          ].map((c,i) => (
            <div key={i}>
              <div className="w-12 h-12 mx-auto rounded-full bg-berulia-rose/50 flex items-center justify-center">
                <c.Icon size={20} className="text-berulia-rosedeep"/>
              </div>
              <p className="font-heading text-xl mt-4 text-berulia-inkdeep">{c.title}</p>
              {c.to ? <Link to={c.to} className="overline link-underline text-berulia-inkmuted">{c.sub}</Link> : <p className="overline text-berulia-inkmuted">{c.sub}</p>}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
